import { useRef } from 'react'
import { Grid3X3, Users, History } from 'lucide-react'
import { shallow } from 'zustand/shallow'
import { cn } from '@/lib/utils'
import { useCallStore, type MakeCallMeta, type SoftphoneTab } from '@/stores/callStore'
import { DialPad } from './DialPad'
import { ContactPickerTab } from './ContactPickerTab'
import { CallHistoryTab } from './CallHistoryTab'

const tabButtonId = (id: SoftphoneTab) => `softphone-tab-btn-${id}`
const tabPanelId = (id: SoftphoneTab) => `softphone-tab-panel-${id}`

// ─── Types ───────────────────────────────────────────────────────────────────

interface SoftphonePanelProps {
  onCall: (number: string, meta?: MakeCallMeta) => void
}

interface TabDef {
  id: SoftphoneTab
  label: string
  icon: typeof Grid3X3
}

const TABS: readonly TabDef[] = [
  { id: 'keypad', label: 'Keypad', icon: Grid3X3 },
  { id: 'contacts', label: 'Contacts', icon: Users },
  { id: 'history', label: 'History', icon: History },
]

// ─── Component ───────────────────────────────────────────────────────────────

/**
 * Tabbed body of the expanded softphone widget.
 * Uses MIDNIGHT segmented-control style tab navigation.
 */
export function SoftphonePanel({ onCall }: SoftphonePanelProps) {
  const { softphoneTab, setSoftphoneTab } = useCallStore(
    (s) => ({
      softphoneTab: s.softphoneTab,
      setSoftphoneTab: s.setSoftphoneTab,
    }),
    shallow,
  )

  const tabRefs = useRef<Partial<Record<SoftphoneTab, HTMLButtonElement | null>>>({})

  const activateTab = (id: SoftphoneTab) => {
    setSoftphoneTab(id)
    tabRefs.current[id]?.focus()
  }

  const handleTabKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    const idx = TABS.findIndex((t) => t.id === softphoneTab)
    if (idx === -1) return
    let next = idx
    switch (e.key) {
      case 'ArrowRight':
        next = (idx + 1) % TABS.length
        break
      case 'ArrowLeft':
        next = (idx - 1 + TABS.length) % TABS.length
        break
      case 'Home':
        next = 0
        break
      case 'End':
        next = TABS.length - 1
        break
      default:
        return
    }
    e.preventDefault()
    activateTab(TABS[next].id)
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {/* Segmented tab control — MIDNIGHT .ds-segment */}
      <div
        style={{
          padding: '10px 12px 10px',
          borderBottom: '0.5px solid var(--midnight-border)',
          background: 'var(--midnight-surface)',
          flexShrink: 0,
        }}
      >
        <div
          role="tablist"
          aria-label="Softphone"
          className="ds-segment"
          style={{ display: 'flex', width: '100%' }}
        >
          {TABS.map((tab) => {
            const Icon = tab.icon
            const active = tab.id === softphoneTab
            return (
              <button
                key={tab.id}
                id={tabButtonId(tab.id)}
                ref={(el) => { tabRefs.current[tab.id] = el }}
                role="tab"
                type="button"
                aria-selected={active}
                aria-controls={tabPanelId(tab.id)}
                tabIndex={active ? 0 : -1}
                onClick={() => setSoftphoneTab(tab.id)}
                onKeyDown={handleTabKeyDown}
                className={cn('ds-segment-item', active && 'ds-segment-item-active')}
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  touchAction: 'manipulation',
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                <Icon style={{ width: 13, height: 13 }} />
                {tab.label}
              </button>
            )
          })}
        </div>
      </div>

      {/* Tab body — uniform min-height ensures all tabs render at the same size.
          The body fills available space when inside a full-screen PhoneSheet
          (flex:1) and stays at min-height when inside the desktop popover. */}
      <div
        role="tabpanel"
        id={tabPanelId(softphoneTab)}
        aria-labelledby={tabButtonId(softphoneTab)}
        tabIndex={0}
        style={{
          outline: 'none',
          background: 'var(--midnight-surface)',
          flex: 1,
          minHeight: 360,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {softphoneTab === 'keypad' && <DialPad onCall={onCall} />}
        {softphoneTab === 'contacts' && <ContactPickerTab onCall={onCall} />}
        {softphoneTab === 'history' && <CallHistoryTab />}
      </div>
    </div>
  )
}
